import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  Refrigerator,
  Snowflake,
  Package,
  Leaf,
  AlertTriangle,
  Trash2,
  Edit2,
  Clock,
  Lightbulb,
  Lock,
  Sparkles,
  Loader2,
  ChefHat,
  ScanBarcode,
  Camera,
  X,
  Receipt,
  Upload,
  Check,
  ImageIcon
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

const CATEGORIES = [
  { value: 'fridge', label: 'Fridge', icon: Refrigerator, color: 'text-blue-500' },
  { value: 'freezer', label: 'Freezer', icon: Snowflake, color: 'text-cyan-500' },
  { value: 'pantry', label: 'Pantry', icon: Package, color: 'text-amber-500' },
  { value: 'spices', label: 'Spices', icon: Leaf, color: 'text-green-500' },
];

const PantrySection = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProPlus, setIsProPlus] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expiringItems, setExpiringItems] = useState([]);
  const [useItUpRecipes, setUseItUpRecipes] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  
  // Barcode scanning state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  
  // Receipt scanning state
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [scanningReceipt, setScanningReceipt] = useState(false);
  const [scannedItems, setScannedItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [addingFromReceipt, setAddingFromReceipt] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    quantity: 1,
    unit: 'item',
    category: 'fridge',
    expiration_date: '',
    notes: ''
  });

  // Check subscription and load pantry
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      
      try {
        // Check subscription
        const subResponse = await api.get('/subscription/status');
        const isPP = subResponse.data.is_pro && subResponse.data.plan === 'plus';
        setIsProPlus(isPP);
        
        if (isPP) {
          // Load pantry items
          const pantryResponse = await api.get('/pantry');
          setItems(pantryResponse.data);
          
          // Load expiring items
          const expiringResponse = await api.get('/pantry/expiring?days=5');
          setExpiringItems(expiringResponse.data);
          
          // Load use-it-up recipes
          try {
            const recipesResponse = await api.get('/pantry/use-it-up');
            setUseItUpRecipes(recipesResponse.data.recipes || []);
          } catch (e) {
            // Ignore if no expiring items
          }
        }
      } catch (error) {
        console.error('Failed to load pantry:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [api, isAuthenticated]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/pantry/${editingItem.id}`, formData);
        toast.success('Item updated!');
      } else {
        await api.post('/pantry', formData);
        toast.success('Item added to pantry!');
      }
      
      // Reload items
      const response = await api.get('/pantry');
      setItems(response.data);
      
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await api.delete(`/pantry/${itemId}`);
      setItems(items.filter(i => i.id !== itemId));
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      quantity: 1,
      unit: 'item',
      category: 'fridge',
      expiration_date: '',
      notes: ''
    });
    setEditingItem(null);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      quantity: item.quantity,
      unit: item.unit,
      category: item.category,
      expiration_date: item.expiration_date?.split('T')[0] || '',
      notes: item.notes || ''
    });
    setDialogOpen(true);
  };

  // Barcode scanning functions
  const startScanner = async () => {
    setScannerOpen(true);
    setScanning(true);
    
    // Small delay to let the dialog render
    setTimeout(async () => {
      try {
        const html5QrCode = new Html5Qrcode("barcode-reader");
        html5QrCodeRef.current = html5QrCode;
        
        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 150 },
            aspectRatio: 1.5
          },
          async (decodedText) => {
            // Barcode scanned successfully
            await stopScanner();
            await lookupBarcode(decodedText);
          },
          (errorMessage) => {
            // Scan error (ignore, keep scanning)
          }
        );
      } catch (err) {
        console.error("Scanner error:", err);
        toast.error("Could not access camera. Please check permissions.");
        setScannerOpen(false);
        setScanning(false);
      }
    }, 100);
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setScanning(false);
    setScannerOpen(false);
  };

  const lookupBarcode = async (barcode) => {
    setLookingUp(true);
    try {
      const response = await api.get(`/pantry/barcode/${barcode}`);
      
      if (response.data.found) {
        // Pre-fill the form with product data
        setFormData({
          name: response.data.brand 
            ? `${response.data.brand} ${response.data.name}` 
            : response.data.name,
          quantity: 1,
          unit: 'item',
          category: response.data.category || 'pantry',
          expiration_date: '',
          notes: response.data.quantity_info || ''
        });
        
        toast.success(`Found: ${response.data.name}`, {
          description: response.data.brand || 'Product identified'
        });
        
        setDialogOpen(true);
      } else {
        // Product not found - let user enter manually
        toast.info('Product not found', {
          description: 'Please enter the details manually'
        });
        setFormData({
          ...formData,
          name: '',
          notes: `Barcode: ${barcode}`
        });
        setDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to look up barcode');
      setDialogOpen(true);
    } finally {
      setLookingUp(false);
    }
  };

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Receipt scanning functions
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Please use an image under 10MB.');
      return;
    }
    
    setReceiptImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setReceiptPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const scanReceipt = async () => {
    if (!receiptImage) {
      toast.error('Please select a receipt image first');
      return;
    }
    
    setScanningReceipt(true);
    setScannedItems([]);
    setSelectedItems({});
    
    try {
      // Convert image to base64
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(receiptImage);
      const imageBase64 = await base64Promise;
      
      // Call the API
      const response = await api.post('/pantry/scan-receipt', {
        image_base64: imageBase64
      });
      
      if (response.data.success && response.data.items.length > 0) {
        setScannedItems(response.data.items);
        // Select all items by default
        const selected = {};
        response.data.items.forEach((item, index) => {
          selected[index] = true;
        });
        setSelectedItems(selected);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || 'No items found on receipt');
      }
    } catch (error) {
      console.error('Receipt scan error:', error);
      toast.error(error.response?.data?.detail || 'Failed to scan receipt. Please try again.');
    } finally {
      setScanningReceipt(false);
    }
  };

  const toggleItemSelection = (index) => {
    setSelectedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const addSelectedItems = async () => {
    const itemsToAdd = scannedItems.filter((_, index) => selectedItems[index]);
    
    if (itemsToAdd.length === 0) {
      toast.error('Please select at least one item to add');
      return;
    }
    
    setAddingFromReceipt(true);
    
    try {
      const response = await api.post('/pantry/bulk-add', itemsToAdd.map(item => ({
        name: item.name,
        quantity: item.quantity,
        unit: item.unit,
        category: item.category
      })));
      
      toast.success(response.data.message);
      
      // Reload pantry items
      const pantryResponse = await api.get('/pantry');
      setItems(pantryResponse.data);
      
      // Close dialog and reset state
      closeReceiptDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add items');
    } finally {
      setAddingFromReceipt(false);
    }
  };

  const closeReceiptDialog = () => {
    setReceiptDialogOpen(false);
    setReceiptImage(null);
    setReceiptPreview(null);
    setScannedItems([]);
    setSelectedItems({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateScannedItem = (index, field, value) => {
    setScannedItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const getExpiryBadge = (item) => {
    if (!item.days_until_expiry && item.days_until_expiry !== 0) return null;
    
    if (item.days_until_expiry < 0) {
      return <Badge variant="destructive" className="text-xs">Expired</Badge>;
    } else if (item.days_until_expiry === 0) {
      return <Badge variant="destructive" className="text-xs">Today!</Badge>;
    } else if (item.days_until_expiry <= 3) {
      return <Badge className="bg-orange-500 text-xs">{item.days_until_expiry}d left</Badge>;
    } else if (item.days_until_expiry <= 7) {
      return <Badge variant="secondary" className="text-xs">{item.days_until_expiry}d</Badge>;
    }
    return null;
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    if (!cat) return Package;
    return cat.icon;
  };

  const filteredItems = activeTab === 'all' 
    ? items 
    : items.filter(item => item.category === activeTab);

  // Not authenticated
  if (!isAuthenticated) return null;

  // Loading
  if (loading) {
    return (
      <Card className="border-border/50">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Not Pro Plus - show upgrade prompt
  if (!isProPlus) {
    return (
      <Card className="border-border/50 bg-gradient-to-br from-primary/5 to-accent/5">
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Package className="h-5 w-5" />
            Pantry Intelligence
            <Badge variant="secondary" className="ml-2">Pro Plus</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-primary/50" />
            </div>
            <h3 className="font-semibold mb-2">Track What&apos;s in Your Kitchen</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              Never buy duplicates or let food go to waste. Get expiration alerts and recipe suggestions based on what you have.
            </p>
            <Button onClick={() => navigate('/subscription')}>
              <Sparkles className="h-4 w-4 mr-2" />
              Upgrade to Pro Plus
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <Package className="h-5 w-5" />
            My Pantry
            <Badge variant="outline" className="ml-2">{items.length} items</Badge>
          </CardTitle>
          <div className="flex gap-2">
            {/* Scan Receipt Button */}
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setReceiptDialogOpen(true)}
              data-testid="scan-receipt-btn"
            >
              <Receipt className="h-4 w-4 mr-1" />
              Receipt
            </Button>
            
            {/* Scan Barcode Button */}
            <Button 
              size="sm" 
              variant="outline"
              onClick={startScanner}
              disabled={scanning || lookingUp}
              data-testid="scan-barcode-btn"
            >
              {lookingUp ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <ScanBarcode className="h-4 w-4 mr-1" />
              )}
              Scan
            </Button>
            
            {/* Add Item Button */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-pantry-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-['Playfair_Display']">
                    {editingItem ? 'Edit Item' : 'Add to Pantry'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingItem ? 'Update item details' : 'Add an item to track in your pantry'}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Item Name</Label>
                    <Input
                      placeholder="e.g., Milk, Chicken breast, Pasta"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="pantry-item-name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="item">item(s)</SelectItem>
                        <SelectItem value="lb">lb</SelectItem>
                        <SelectItem value="oz">oz</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                        <SelectItem value="cup">cup(s)</SelectItem>
                        <SelectItem value="tbsp">tbsp</SelectItem>
                        <SelectItem value="tsp">tsp</SelectItem>
                        <SelectItem value="bottle">bottle(s)</SelectItem>
                        <SelectItem value="can">can(s)</SelectItem>
                        <SelectItem value="box">box(es)</SelectItem>
                        <SelectItem value="bag">bag(s)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <cat.icon className={`h-4 w-4 ${cat.color}`} />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Expiration Date
                    <span className="text-xs text-muted-foreground font-normal">(auto-filled if empty)</span>
                  </Label>
                  <Input
                    type="date"
                    value={formData.expiration_date}
                    onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input
                    placeholder="e.g., Organic, Brand name"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button onClick={handleSubmit} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingItem ? 'Update' : 'Add Item')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Expiring Items Alert */}
        {expiringItems.length > 0 && (
          <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <span className="font-medium text-sm">{expiringItems.length} item(s) expiring soon</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {expiringItems.slice(0, 5).map(item => (
                <Badge key={item.id} variant="outline" className="text-xs">
                  {item.name} ({item.days_until_expiry < 0 ? 'expired' : `${item.days_until_expiry}d`})
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Use It Up Recipes */}
        {useItUpRecipes.length > 0 && (
          <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Use it up! Recipe ideas:</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {useItUpRecipes.slice(0, 3).map(recipe => (
                <div 
                  key={recipe.id} 
                  className="flex-shrink-0 w-32 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate(`/recipes/${recipe.id}`)}
                >
                  <img 
                    src={recipe.image} 
                    alt={recipe.title}
                    className="w-full h-20 object-cover rounded-md"
                  />
                  <p className="text-xs mt-1 line-clamp-2">{recipe.title}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 h-9">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            {CATEGORIES.map(cat => (
              <TabsTrigger key={cat.value} value={cat.value} className="text-xs">
                <cat.icon className={`h-3 w-3 mr-1 ${cat.color}`} />
                {cat.label}
              </TabsTrigger>
            ))}
          </TabsList>
          
          <TabsContent value={activeTab} className="mt-3">
            {filteredItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No items in {activeTab === 'all' ? 'your pantry' : `your ${activeTab}`}</p>
                <Button variant="link" size="sm" onClick={() => setDialogOpen(true)}>
                  Add your first item
                </Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredItems.map(item => {
                  const Icon = getCategoryIcon(item.category);
                  return (
                    <div 
                      key={item.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 group"
                    >
                      <Icon className={`h-4 w-4 ${CATEGORIES.find(c => c.value === item.category)?.color || 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{item.name}</span>
                          {getExpiryBadge(item)}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {item.quantity} {item.unit}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7"
                          onClick={() => openEditDialog(item)}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      
      {/* Barcode Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={(open) => { if (!open) stopScanner(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
              <ScanBarcode className="h-5 w-5" />
              Scan Barcode
            </DialogTitle>
            <DialogDescription>
              Point your camera at a product barcode
            </DialogDescription>
          </DialogHeader>
          
          <div className="relative">
            {/* Scanner viewport */}
            <div 
              id="barcode-reader" 
              ref={scannerRef}
              className="w-full overflow-hidden rounded-lg bg-black"
              style={{ minHeight: '250px' }}
            />
            
            {/* Scanning overlay */}
            {scanning && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="border-2 border-primary rounded-lg w-64 h-24 animate-pulse" />
              </div>
            )}
          </div>
          
          <div className="text-center text-sm text-muted-foreground">
            <p>Align the barcode within the frame</p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={stopScanner} className="w-full">
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Receipt Scan Dialog */}
      <Dialog open={receiptDialogOpen} onOpenChange={(open) => { if (!open) closeReceiptDialog(); else setReceiptDialogOpen(true); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Scan Receipt
            </DialogTitle>
            <DialogDescription>
              Upload a photo of your grocery receipt to add items automatically
            </DialogDescription>
          </DialogHeader>
          
          {/* Upload Section */}
          {scannedItems.length === 0 && (
            <div className="space-y-4 py-4">
              {/* File Input */}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                ref={fileInputRef}
                className="hidden"
                data-testid="receipt-file-input"
              />
              
              {/* Upload Area */}
              {!receiptPreview ? (
                <div 
                  className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="receipt-upload-area"
                >
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Upload className="h-6 w-6 text-primary" />
                  </div>
                  <p className="font-medium mb-1">Upload Receipt Photo</p>
                  <p className="text-sm text-muted-foreground">
                    Click to browse or take a photo
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative rounded-lg overflow-hidden bg-muted">
                    <img 
                      src={receiptPreview} 
                      alt="Receipt preview" 
                      className="w-full h-48 object-contain"
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      className="absolute top-2 right-2 h-7 w-7"
                      onClick={() => {
                        setReceiptImage(null);
                        setReceiptPreview(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={scanReceipt}
                    disabled={scanningReceipt}
                    data-testid="scan-receipt-submit-btn"
                  >
                    {scanningReceipt ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analyzing receipt...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Extract Items with AI
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              <div className="text-center text-xs text-muted-foreground">
                <p>Supports JPEG, PNG images up to 10MB</p>
              </div>
            </div>
          )}
          
          {/* Scanned Items Review */}
          {scannedItems.length > 0 && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Found {scannedItems.length} items
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const allSelected = Object.values(selectedItems).every(v => v);
                    const newSelected = {};
                    scannedItems.forEach((_, i) => { newSelected[i] = !allSelected; });
                    setSelectedItems(newSelected);
                  }}
                >
                  {Object.values(selectedItems).every(v => v) ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {scannedItems.map((item, index) => {
                  const CategoryIcon = getCategoryIcon(item.category);
                  return (
                    <div 
                      key={index}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        selectedItems[index] ? 'bg-primary/5 border-primary/30' : 'bg-muted/30 border-transparent'
                      }`}
                    >
                      <Checkbox
                        checked={selectedItems[index] || false}
                        onCheckedChange={() => toggleItemSelection(index)}
                        data-testid={`receipt-item-checkbox-${index}`}
                      />
                      <CategoryIcon className={`h-4 w-4 flex-shrink-0 ${
                        CATEGORIES.find(c => c.value === item.category)?.color || 'text-muted-foreground'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <Input
                          value={item.name}
                          onChange={(e) => updateScannedItem(index, 'name', e.target.value)}
                          className="h-7 text-sm font-medium"
                          data-testid={`receipt-item-name-${index}`}
                        />
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateScannedItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                            className="h-6 w-16 text-xs"
                            min="0.1"
                            step="0.5"
                          />
                          <Select 
                            value={item.category} 
                            onValueChange={(v) => updateScannedItem(index, 'category', v)}
                          >
                            <SelectTrigger className="h-6 w-24 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {CATEGORIES.map(cat => (
                                <SelectItem key={cat.value} value={cat.value} className="text-xs">
                                  {cat.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {item.price && (
                            <span className="text-xs text-muted-foreground">
                              ${item.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setScannedItems([]);
                    setSelectedItems({});
                  }}
                >
                  Start Over
                </Button>
                <Button
                  className="flex-1"
                  onClick={addSelectedItems}
                  disabled={addingFromReceipt || !Object.values(selectedItems).some(v => v)}
                  data-testid="add-receipt-items-btn"
                >
                  {addingFromReceipt ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Add {Object.values(selectedItems).filter(v => v).length} Items
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter className="sm:justify-start">
            <Button variant="outline" onClick={closeReceiptDialog} className="w-full sm:w-auto">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PantrySection;
